import { useState, useEffect, useCallback, useRef } from "react";
import { useDispatch, useSelector } from "react-redux";
import axios from "axios";
import AWS from "aws-sdk";
import { Auth } from "aws-amplify";
import {
  setCreds,
  setId,
  signInUser,
  setManagerInfo,
} from "../../Features/userSlice";
import { setbID } from "../../Features/guestSlice";
import {
  rememberGuestBooking,
  setGuestStayEnd,
  forgetGuestStay,
} from "../../Helper/guestStay";
import { apiGetAuth } from "../../Backend/api";
import { parseGuestAppConfig } from "../../Helper/guestAppConfig";
import { forgetPropertyLink } from "../../Helper/propertyLink";

AWS.config.update({
  region: "us-west-2",
  credentials: new AWS.Credentials(
    process.env.REACT_APP_PUBLIC_KEY,
    process.env.REACT_APP_SECRET_KEY
  ),
});

const safeDispatch = (dispatch, action) => {
  try {
    dispatch(action);
  } catch (error) {
    if (error.message && error.message.includes("QuotaExceededError")) {
      console.warn("Storage quota exceeded, clearing old data...");
      try {
        localStorage.removeItem("persist:root");
        dispatch(action);
      } catch (retryError) {
        console.warn("Failed to dispatch after cleanup:", retryError);
      }
    } else {
      throw error;
    }
  }
};

/**
 * Shared booking bootstrap used by /go (landing) and /upsell.
 * Loads owner, signs the guest in, and fetches /bookings/details/:bID.
 */
export const useBookingBootstrap = (bID) => {
  const [manager, setManager] = useState({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [bookingDetails, setBookingDetails] = useState(null);
  const [isLinkExpired, setIsLinkExpired] = useState(false);

  const dispatch = useDispatch();
  const currentUser = useSelector((state) => state.user);

  const hasInitialized = useRef(false);
  const hasCheckedUser = useRef(false);
  const linkExpiredRef = useRef(false);

  useEffect(() => {
    try {
      const keysToKeep = [
        "amplify-signin-with-hostedUI",
        "amplify-cache",
        "aws.cognito.identity-id",
        "aws.cognito.identity-pool-id",
        "pendingVerification",
      ];

      const keysToRemove = [];
      for (let i = 0; i < localStorage.length; i++) {
        const key = localStorage.key(i);
        if (
          key &&
          !keysToKeep.includes(key) &&
          !key.startsWith("amplify-") &&
          !key.startsWith("aws.")
        ) {
          keysToRemove.push(key);
        }
      }

      keysToRemove.forEach((key) => {
        try {
          localStorage.removeItem(key);
        } catch (e) {
          // Silent cleanup
        }
      });
    } catch (error) {
      // Silent cleanup
    }

    if (bID) {
      forgetPropertyLink();
      rememberGuestBooking(bID);
      safeDispatch(dispatch, setbID(bID));
      safeDispatch(dispatch, setId(bID));
    }
  }, [dispatch, bID]);

  useEffect(() => {
    if (!hasCheckedUser.current) {
      hasCheckedUser.current = true;
      safeDispatch(dispatch, signInUser());
    }
  }, [dispatch]);

  const handleDownload = useCallback(async (key) => {
    if (!key) return;
    if (key.startsWith("http")) return key;
    const cloudfrontBaseUrl = "https://dl6ri8ttf37m3.cloudfront.net";
    return `${cloudfrontBaseUrl}/public/${encodeURIComponent(
      key
    )}?width=600&format=webp`;
  }, []);

  const fetchBookingDetails = useCallback(
    async (reservationID) => {
      try {
        let session;
        try {
          session = await Auth.currentSession();
        } catch (authError) {
          console.log("No current session, skipping booking details fetch");
          return null;
        }

        if (!session || !session.isValid()) {
          console.log("Session is invalid, skipping booking details fetch");
          return null;
        }

        // The endpoint answers with an ETag and no Cache-Control, so the
        // browser is free to reuse a cached body under heuristic freshness.
        // This response is the source of truth for access gating and the
        // host's guest-app settings, so it must never be served stale — a
        // unique URL per fetch opts out of the HTTP cache entirely.
        const responseData = await apiGetAuth(
          `/bookings/details/${reservationID}`,
          { _ts: Date.now() }
        );

        if (responseData.success) {
          const bookingData = responseData.data;

          if (bookingData.booking && bookingData.booking.isLinkActive === false) {
            console.log("Link has expired for booking:", reservationID);
            linkExpiredRef.current = true;
            setIsLinkExpired(true);
            forgetGuestStay();
            return null;
          }

          setGuestStayEnd(
            reservationID,
            bookingData.booking?.checkOutDate,
            bookingData.owner?.timezone
          );

          setBookingDetails(bookingData);

          if (bookingData.owner && bookingData.owner.accountMetas) {
            setManager((prev) => ({
              ...prev,
              accountMetas: bookingData.owner.accountMetas,
            }));
            safeDispatch(
              dispatch,
              setManagerInfo({
                accountMetas: bookingData.owner.accountMetas,
              })
            );
          }

          safeDispatch(dispatch, {
            type: "booking/setBookingDetails",
            payload: bookingData,
          });

          return bookingData;
        }

        console.error("Failed to fetch booking details:", responseData);
        return null;
      } catch (error) {
        console.error("Error fetching booking details:", error);
        return null;
      }
    },
    [dispatch]
  );

  const decryptPassword = useCallback(async (encryptedPassword) => {
    const response = await axios.post(
      "https://api.linkbase.ca/auth/decrypt-password",
      { encryptedPassword },
      {
        headers: {
          "Content-Type": "application/json",
          "x-api-key": process.env.REACT_APP_LINKBASE_API_KEY,
        },
      }
    );

    if (!response.data.data._p) {
      throw new Error("Decryption returned empty password");
    }

    return atob(response.data.data._p);
  }, []);

  const signInUserLogic = useCallback(
    async (email, password) => {
      try {
        const user = await Auth.signIn(email, password);
        safeDispatch(dispatch, signInUser());
        return user;
      } catch (err) {
        console.error("Background sign in error:", err);
        throw err;
      }
    },
    [dispatch]
  );

  const fetchBookingData = useCallback(async () => {
    if (hasInitialized.current || !bID) return;
    hasInitialized.current = true;

    try {
      setLoading(true);
      setError(null);

      const [bookingResponse] = await Promise.allSettled([
        axios.get(`https://api.linkbase.ca/bookings/check-existence/${bID}`, {
          headers: {
            "x-api-key": process.env.REACT_APP_LINKBASE_API_KEY,
            "Content-Type": "application/json",
          },
        }),
      ]);

      if (bookingResponse.status !== "fulfilled") {
        setError("Failed to load booking information");
        return;
      }

      const data = bookingResponse.value.data;
      if (!(data.success && data.data.exists)) {
        setError("Booking not found or invalid");
        forgetGuestStay();
        return;
      }

      const owner = data.data.owner;

      setManager({
        name: owner.name,
        email: owner.email,
        images: null,
        stripeId: owner.stripeId,
        password: owner.password,
        accountMetas: owner.accountMetas || null,
        extendedMetas: owner.extendedMetas || null,
      });

      safeDispatch(
        dispatch,
        setCreds({
          email: owner.email,
          password: owner.password,
          stripeId: owner.stripeId,
        })
      );

      safeDispatch(
        dispatch,
        setManagerInfo({
          name: owner.name,
          email: owner.email,
          images: null,
          stripeId: owner.stripeId,
          password: owner.password,
          accountMetas: owner.accountMetas || null,
          extendedMetas: owner.extendedMetas || null,
        })
      );

      if (owner.images) {
        handleDownload(owner.images)
          .then((imageUrl) => {
            setManager((prev) => ({ ...prev, images: imageUrl }));
            safeDispatch(
              dispatch,
              setManagerInfo({
                name: owner.name,
                images: imageUrl,
              })
            );
          })
          .catch(() => {
            const fallbackUrl = `https://linkbase-admin.s3.us-west-2.amazonaws.com/public/${owner.images}`;
            setManager((prev) => ({ ...prev, images: fallbackUrl }));
            safeDispatch(
              dispatch,
              setManagerInfo({
                name: owner.name,
                images: fallbackUrl,
              })
            );
          });
      }

      const authPromise = (async () => {
        try {
          let currentAuthUser;
          try {
            currentAuthUser = await Auth.currentAuthenticatedUser();
          } catch (authError) {
            const decryptedPassword = await decryptPassword(owner.password);
            currentAuthUser = await signInUserLogic(
              owner.email,
              decryptedPassword
            );
          }

          if (currentAuthUser && bID) {
            // The Cognito session can lag a beat behind sign-in, and a
            // transient failure here used to leave `bookingDetails` null for
            // the rest of the page load: nothing retried, despite the old
            // "will retry on next auth check" comment, and the
            // `hasInitialized` guard blocks a second pass. That is what
            // forced guests to reload until a fetch happened to land. Retry
            // briefly instead, so a single load is always enough.
            for (let attempt = 0; attempt < 4; attempt++) {
              if (linkExpiredRef.current) break;

              try {
                const session = await Auth.currentSession();
                if (session && session.isValid()) {
                  const details = await fetchBookingDetails(bID);
                  if (details || linkExpiredRef.current) break;
                }
              } catch (sessionError) {
                // Session not ready yet — fall through to the backoff.
              }

              await new Promise((resolve) =>
                setTimeout(resolve, 400 * (attempt + 1))
              );
            }
          }
        } catch (authError) {
          console.error("Background auth failed:", authError);
        }
      })();

      authPromise.catch(() => {});
    } catch (error) {
      console.error("Error fetching booking data:", error);
      setError("Failed to load booking information");
    } finally {
      setLoading(false);
    }
  }, [
    bID,
    dispatch,
    handleDownload,
    decryptPassword,
    signInUserLogic,
    fetchBookingDetails,
  ]);

  useEffect(() => {
    if (bID) fetchBookingData();
  }, [bID, fetchBookingData]);

  return {
    bID,
    manager,
    loading,
    error,
    currentUser,
    bookingDetails,
    isLinkExpired,
  };
};

export const buildRoomWithTimezone = (bookingDetails) => {
  const ownerId = bookingDetails?.room?.owner
    ? bookingDetails.room.owner.split("::")[0]
    : null;

  return {
    ...bookingDetails.room,
    owner: {
      id: ownerId,
      timezone: bookingDetails.owner?.timezone || "America/Los_Angeles",
    },
  };
};

export const buildBookingForState = (bookingDetails) => {
  const stepsDone = bookingDetails.securityStepsComplete === true;
  return {
    ...bookingDetails.booking,
    securityStepsComplete: stepsDone,
    // Re-read from the details response on every fetch — never persisted,
    // so a host flipping the setting lands on the guest's next load.
    guestApp: parseGuestAppConfig(bookingDetails),
  };
};

export const isOnboardingComplete = (bookingDetails) => {
  try {
    const status = bookingDetails?.booking?.status;
    const parsed = typeof status === "string" ? JSON.parse(status) : status;
    return parsed?.done === true;
  } catch {
    return false;
  }
};
